import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { CalendarClock, ImageIcon, Loader2, Minus, Plus, Sparkles, X } from "lucide-react";
import {
  ON_DEMAND_CAMPAIGN_MAX_ITEMS,
  type OnDemandCampaignCreateRequestV1,
} from "@runcheetah/shared";

import {
  ON_DEMAND_CAMPAIGN_FORMATS,
  buildOnDemandCampaignCreateRequest,
  buildSuggestedCampaignPrompt,
  createDefaultOnDemandCampaignCounts,
  getOnDemandCampaignCountTotal,
  type OnDemandCampaignPostType,
  updateOnDemandCampaignCount,
} from "@/lib/on-demand-campaigns";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { RunConfirmationDialog } from "@/components/runs/run-confirmation-dialog";
import { Button, Card, CardContent, Input, Textarea } from "@runcheetah/ui";

type CreateCampaignStep = "brief" | "schedule";

const toInitialDatetimeLocal = (): string => {
  const date = new Date(Date.now() + 24 * 60 * 60 * 1000);
  date.setMinutes(0, 0, 0);
  return date.toISOString().slice(0, 16);
};

const fallbackQuoteRequest: OnDemandCampaignCreateRequestV1 = {
  version: 1,
  prompt: "Run quote",
  generationMode: "preview_first",
  items: [
    {
      scheduledFor: new Date(0).toISOString(),
      platform: "instagram",
      postType: "feed_portrait",
    },
  ],
};

export function CreateCampaignModal({
  brandId,
  brandName,
  open,
  onOpenChange,
  onQueued,
  onUpgradeRequired,
  upgradeLabel,
}: {
  brandId: string;
  brandName: string | null | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onQueued?: () => void;
  onUpgradeRequired?: () => void;
  upgradeLabel?: string;
}) {
  const intl = useIntl();
  const utils = trpc.useUtils();
  const [step, setStep] = useState<CreateCampaignStep>("brief");
  const [prompt, setPrompt] = useState("");
  const [counts, setCounts] = useState(createDefaultOnDemandCampaignCounts);
  const [firstScheduledFor, setFirstScheduledFor] = useState(toInitialDatetimeLocal);
  const [pendingRequest, setPendingRequest] = useState<OnDemandCampaignCreateRequestV1 | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const createCampaign = trpc.campaign.createOnDemand.useMutation({
    onSuccess: () => {
      onQueued?.();
      void utils.campaign.list.invalidate({ brandId });
      void utils.campaign.nextGenerationCheck.invalidate({ brandId });
      onOpenChange(false);
      setStep("brief");
      setPrompt("");
      setCounts(createDefaultOnDemandCampaignCounts());
      setFirstScheduledFor(toInitialDatetimeLocal());
      setPendingRequest(null);
    },
    onError: (mutationError) => setError(mutationError.message),
  });
  const runQuoteQuery = trpc.campaign.quoteCreateOnDemand.useQuery(
    {
      brandId,
      request: pendingRequest ?? fallbackQuoteRequest,
    },
    { enabled: Boolean(pendingRequest), retry: false },
  );
  const total = getOnDemandCampaignCountTotal(counts);
  const canContinue = Boolean(prompt.trim());
  const canSubmit = Boolean(prompt.trim() && total > 0 && firstScheduledFor);

  if (!open) {
    return null;
  }

  const setCount = (postType: OnDemandCampaignPostType, delta: number) => {
    setCounts((current) =>
      updateOnDemandCampaignCount({
        counts: current,
        postType,
        delta,
        maxTotal: ON_DEMAND_CAMPAIGN_MAX_ITEMS,
      }),
    );
  };

  const close = () => {
    if (!createCampaign.isPending) {
      onOpenChange(false);
      setError(null);
      setPendingRequest(null);
    }
  };

  const submit = () => {
    setError(null);
    setPendingRequest(
      buildOnDemandCampaignCreateRequest({
        prompt,
        counts,
        firstScheduledFor,
        intervalHours: 24,
      }),
    );
  };

  const confirmSubmit = () => {
    if (!pendingRequest) {
      return;
    }

    createCampaign.mutate({ brandId, request: pendingRequest });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/75 p-3 backdrop-blur-sm sm:p-4">
      <Card className="max-h-[calc(100dvh-1.5rem)] w-full max-w-3xl overflow-y-auto rounded-3xl border-border/70 bg-card/95 shadow-deep sm:max-h-[calc(100dvh-2rem)]">
        <CardContent className="space-y-6 p-6 sm:p-8">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-3">
              <div className="inline-flex items-center gap-2 rounded-full border border-primary/12 bg-primary/6 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-primary">
                <Sparkles className="size-3.5" aria-hidden="true" />
                <FormattedMessage id="createCampaignModal.badge" defaultMessage="Campaign brief" />
              </div>
              <div className="space-y-2">
                <h2 className="font-display text-3xl font-bold tracking-tight text-foreground">
                  <FormattedMessage id="createCampaignModal.title" defaultMessage="New campaign" />
                </h2>
                <p className="max-w-2xl text-sm leading-6 text-muted-foreground">
                  <FormattedMessage
                    id="createCampaignModal.description"
                    defaultMessage="Turn one strategic direction into a scheduled Instagram campaign."
                  />
                </p>
              </div>
            </div>
            <Button type="button" size="icon" variant="ghost" onClick={close}>
              <X className="size-5" aria-hidden="true" />
              <span className="sr-only">
                <FormattedMessage id="common.close" defaultMessage="Close" />
              </span>
            </Button>
          </div>

          <div className="inline-flex w-full rounded-2xl bg-muted/70 p-1 shadow-xs sm:w-fit">
            <button
              type="button"
              onClick={() => setStep("brief")}
              className={cn(
                "flex-1 rounded-xl px-4 py-2 text-sm font-semibold text-muted-foreground transition-colors sm:flex-none",
                step === "brief" && "bg-card text-primary shadow-sm",
              )}
            >
              <FormattedMessage
                id="createCampaignModal.step.direction"
                defaultMessage="1. Direction"
              />
            </button>
            <button
              type="button"
              onClick={() => canContinue && setStep("schedule")}
              disabled={!canContinue}
              className={cn(
                "flex-1 rounded-xl px-4 py-2 text-sm font-semibold text-muted-foreground transition-colors disabled:cursor-not-allowed disabled:opacity-50 sm:flex-none",
                step === "schedule" && "bg-card text-primary shadow-sm",
              )}
            >
              <FormattedMessage
                id="createCampaignModal.step.mixSchedule"
                defaultMessage="2. Mix & schedule"
              />
            </button>
          </div>

          {step === "brief" ? (
            <div className="space-y-5">
              <div className="rounded-3xl border border-border/70 bg-muted/25 p-5 sm:p-6">
                <div className="space-y-2">
                  <div className="text-sm font-semibold text-foreground">
                    <FormattedMessage
                      id="createCampaignModal.promptLabel"
                      defaultMessage="What should Cheetah plan?"
                    />
                  </div>
                  <p className="text-sm leading-6 text-muted-foreground">
                    <FormattedMessage
                      id="createCampaignModal.promptSupport"
                      defaultMessage="Describe the angle, audience, or proof points you want the campaign to carry across its posts."
                    />
                  </p>
                </div>
                <Textarea
                  id="campaign-prompt"
                  value={prompt}
                  onChange={(event) => setPrompt(event.target.value)}
                  rows={8}
                  className="mt-4 min-h-[12rem] rounded-3xl border-border/70 bg-background/85 px-4 py-3 shadow-none"
                  placeholder={intl.formatMessage({
                    id: "createCampaignModal.promptPlaceholder",
                    defaultMessage:
                      "Create a campaign that introduces a new product angle, proves the value with customer evidence, and ends with a clear next step.",
                  })}
                />
                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-full border-border/80 bg-background/80"
                    onClick={() => setPrompt(buildSuggestedCampaignPrompt(intl, brandName))}
                  >
                    <FormattedMessage
                      id="createCampaignModal.useSuggestedBrief"
                      defaultMessage="Use suggested brief"
                    />
                  </Button>
                  <div className="text-xs text-muted-foreground">
                    <FormattedMessage
                      id="createCampaignModal.briefHint"
                      defaultMessage="A clear brief unlocks a stronger mix in the next step."
                    />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="rounded-3xl border border-border/70 bg-card/90 p-1">
                <div className="grid grid-cols-[1fr_auto] px-4 py-3 text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                  <span>
                    <FormattedMessage
                      id="createCampaignModal.instagramFormat"
                      defaultMessage="Instagram format"
                    />
                  </span>
                  <span>
                    <FormattedMessage id="common.count" defaultMessage="Count" />
                  </span>
                </div>
                <div className="space-y-2">
                  {ON_DEMAND_CAMPAIGN_FORMATS.map((format) => (
                    <div
                      key={format.postType}
                      className="grid grid-cols-[1fr_auto] items-center gap-4 rounded-2xl border border-transparent px-4 py-4 transition-colors hover:border-accent/20 hover:bg-muted/35"
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex size-11 items-center justify-center rounded-2xl bg-muted text-muted-foreground">
                          <ImageIcon className="size-5" aria-hidden="true" />
                        </div>
                        <div>
                          <div className="font-semibold text-foreground">
                            {intl.formatMessage(format.label)}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {intl.formatMessage(format.description)}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          size="icon"
                          variant="outline"
                          className="rounded-xl border-border/80 bg-background/80"
                          onClick={() => setCount(format.postType, -1)}
                          disabled={counts[format.postType] === 0}
                        >
                          <Minus className="size-4" aria-hidden="true" />
                        </Button>
                        <div className="w-10 text-center font-semibold tabular-nums text-foreground">
                          {counts[format.postType]}
                        </div>
                        <Button
                          type="button"
                          size="icon"
                          variant="outline"
                          className="rounded-xl border-border/80 bg-background/80"
                          onClick={() => setCount(format.postType, 1)}
                          disabled={total >= ON_DEMAND_CAMPAIGN_MAX_ITEMS}
                        >
                          <Plus className="size-4" aria-hidden="true" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_260px]">
                <div className="rounded-3xl border border-border/70 bg-muted/25 p-5 sm:p-6">
                  <label
                    className="text-sm font-semibold text-foreground"
                    htmlFor="campaign-schedule"
                  >
                    <FormattedMessage
                      id="createCampaignModal.firstPlannedSlot"
                      defaultMessage="First planned slot"
                    />
                  </label>
                  <p className="mt-2 text-sm leading-6 text-muted-foreground">
                    <FormattedMessage
                      id="createCampaignModal.scheduleSupport"
                      defaultMessage="Pick the first publication time and Cheetah will spread the rest of the campaign from there."
                    />
                  </p>
                  <Input
                    id="campaign-schedule"
                    type="datetime-local"
                    value={firstScheduledFor}
                    onChange={(event) => setFirstScheduledFor(event.target.value)}
                    className="mt-4 rounded-2xl border-border/80 bg-background/85"
                  />
                </div>
                <div className="rounded-3xl border border-primary/12 bg-primary/6 p-5 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2 text-sm font-semibold text-primary">
                    <CalendarClock className="size-4" aria-hidden="true" />
                    <FormattedMessage
                      id="createCampaignModal.scheduleCardTitle"
                      defaultMessage="Scheduling rhythm"
                    />
                  </div>
                  <p className="mt-3 leading-6">
                    <FormattedMessage
                      id="createCampaignModal.dailySpacing"
                      defaultMessage="Campaign posts are spaced daily."
                    />
                  </p>
                </div>
              </div>
            </div>
          )}

          {error ? (
            <div className="space-y-3 rounded-2xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              <div>{error}</div>
              {onUpgradeRequired ? (
                <Button type="button" variant="outline" size="sm" onClick={onUpgradeRequired}>
                  <FormattedMessage
                    id="runConfirmation.upgrade"
                    defaultMessage="Upgrade to keep creating"
                  />
                </Button>
              ) : null}
            </div>
          ) : null}

          <div className="flex flex-col gap-3 border-t border-border/70 pt-5 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground">
              <FormattedMessage
                id="createCampaignModal.selectedCount"
                defaultMessage="{total} of {max} campaign posts selected"
                values={{ total, max: ON_DEMAND_CAMPAIGN_MAX_ITEMS }}
              />
            </p>
            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              {step === "schedule" ? (
                <Button type="button" variant="outline" onClick={() => setStep("brief")}>
                  <FormattedMessage id="common.back" defaultMessage="Back" />
                </Button>
              ) : null}
              {step === "brief" ? (
                <Button type="button" onClick={() => setStep("schedule")} disabled={!canContinue}>
                  <FormattedMessage id="common.continue" defaultMessage="Continue" />
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={submit}
                  disabled={!canSubmit || createCampaign.isPending}
                >
                  {createCampaign.isPending ? (
                    <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                  ) : null}
                  <FormattedMessage
                    id="createCampaignModal.generateCampaign"
                    defaultMessage="Generate campaign"
                  />
                </Button>
              )}
            </div>
          </div>

          <RunConfirmationDialog
            open={Boolean(pendingRequest)}
            title={intl.formatMessage({
              id: "createCampaignModal.runConfirmTitle",
              defaultMessage: "Use runs to generate this campaign?",
            })}
            description={intl.formatMessage({
              id: "createCampaignModal.runConfirmDescription",
              defaultMessage:
                "Cheetah will generate one campaign preview now. Approving the campaign later will use runs for the remaining posts.",
            })}
            confirmLabel={intl.formatMessage({
              id: "createCampaignModal.runConfirmAction",
              defaultMessage: "Generate preview and use runs",
            })}
            upgradeLabel={upgradeLabel}
            quote={runQuoteQuery.data ?? null}
            quotePending={runQuoteQuery.isPending || runQuoteQuery.isFetching}
            confirmPending={createCampaign.isPending}
            error={error ?? runQuoteQuery.error?.message ?? null}
            onCancel={() => {
              if (!createCampaign.isPending) {
                setPendingRequest(null);
              }
            }}
            onUpgrade={onUpgradeRequired}
            onConfirm={confirmSubmit}
          />
        </CardContent>
      </Card>
    </div>
  );
}
